import { z } from 'zod';

/** Approved consent patterns (owner may only pick/edit within these). */
export const CONSENT_PATTERNS = {
  default:
    'By sending this, you agree that {Business} may contact you about your request using the details you provided. This is a request for an estimate, not a confirmed appointment.',
  withSms:
    'By sending this, you agree that {Business} may contact you about your request using the details you provided. This is a request for an estimate, not a confirmed appointment. Message and data rates may apply.',
} as const;

export const CONSENT_VERSION = 'v1-2026-10';

export const TIMING_OPTIONS = [
  'Emergency/ASAP',
  'This week',
  'Within a month',
  'Just planning',
] as const;

export const CONTACT_PREFS = ['phone', 'text', 'email'] as const;

export const INQUIRY_STATUSES = ['new', 'contacted', 'closed', 'spam'] as const;
export const CLOSED_REASONS = ['hired', 'not_hired', 'spam', 'no_response'] as const;
export const FORM_STATUSES = ['draft', 'published', 'paused'] as const;
export const RETENTION_MONTHS = [12, 24, 36] as const;

const customQuestionSchema = z.object({
  id: z.string().min(1).max(64),
  label: z.string().min(1).max(200),
  type: z.enum(['short_text', 'select', 'yes_no']),
  options: z.array(z.string().max(100)).max(20).optional(),
});

export const quoteFormConfigSchema = z.object({
  businessName: z.string().min(1).max(120),
  logoUrl: z.string().max(500).optional().nullable(),
  publicPhone: z.string().max(30).optional().nullable(),
  publicReplyEmail: z.string().email().optional().nullable().or(z.literal('')),
  contactPrefsShown: z.array(z.enum(CONTACT_PREFS)).min(1).max(3).default(['phone', 'text', 'email']),
  services: z.array(z.string().min(1).max(80)).max(15).default([]),
  serviceAreaMode: z.enum(['zip_check', 'city_only']).default('zip_check'),
  serviceAreaChips: z.array(z.string().min(1).max(80)).max(100).default([]),
  fields: z
    .object({
      photosEnabled: z.boolean().default(true),
      streetOptional: z.boolean().default(true),
      timingEnabled: z.boolean().default(true),
      contactPrefEnabled: z.boolean().default(true),
      customerConfirmEmail: z.boolean().default(true),
      smsFollowUpWording: z.boolean().default(false),
    })
    .default({}),
  customQuestions: z.array(customQuestionSchema).max(3).default([]),
  consentPattern: z.enum(['default', 'withSms']).default('default'),
  consentTextOverride: z.string().max(800).optional().nullable(),
  successMessage: z
    .string()
    .max(400)
    .default('Thanks, {first name}. {Business} received your request and will contact you.'),
  descriptionPrompt: z
    .string()
    .max(300)
    .default("Tell us what's going on and when it started"),
  wizardStep: z.number().int().min(1).max(5).default(1),
  photoRetentionMonths: z.union([z.literal(6), z.literal(12), z.literal(24), z.literal(36)]).default(6),
  dailyDigest: z.boolean().default(false),
  embed: z
    .object({
      width: z.enum(['full', '480', '640']).default('full'),
      theme: z.enum(['light', 'dark', 'auto']).default('light'),
      accent: z.string().regex(/^#[0-9A-Fa-f]{6}$/).default('#1a5f4a'),
      buttonText: z.string().min(1).max(60).default('Request an estimate'),
    })
    .default({}),
  hidePoweredBy: z.boolean().default(false),
});

export type QuoteFormConfig = z.infer<typeof quoteFormConfigSchema>;
export type CustomQuestion = z.infer<typeof customQuestionSchema>;

export function defaultQuoteFormConfig(partial?: Partial<QuoteFormConfig>): QuoteFormConfig {
  return quoteFormConfigSchema.parse({
    businessName: partial?.businessName ?? 'Your business',
    ...partial,
  });
}

export function resolveConsentText(config: QuoteFormConfig): string {
  const pattern =
    config.consentPattern === 'withSms' || config.fields.smsFollowUpWording
      ? CONSENT_PATTERNS.withSms
      : CONSENT_PATTERNS.default;
  const base = (config.consentTextOverride?.trim() || pattern).replace(
    /\{Business\}/g,
    config.businessName,
  );
  return base;
}

export function resolveSuccessMessage(config: QuoteFormConfig, firstName: string): string {
  return config.successMessage
    .replace(/\{first name\}/gi, firstName || 'there')
    .replace(/\{Business\}/g, config.businessName);
}

export function parseQuoteFormConfig(raw: string | null | undefined): QuoteFormConfig {
  let parsed: unknown = {};
  try {
    parsed = raw ? JSON.parse(raw) : {};
  } catch {
    parsed = {};
  }
  const result = quoteFormConfigSchema.safeParse(parsed);
  if (result.success) return result.data;
  return defaultQuoteFormConfig(
    typeof parsed === 'object' && parsed ? (parsed as Partial<QuoteFormConfig>) : undefined,
  );
}

/** Unpredictable public slug (resistant to trivial enumeration). */
export function generateQuoteSlug(): string {
  const bytes = new Uint8Array(9);
  crypto.getRandomValues(bytes);
  const alphabet = 'abcdefghijklmnopqrstuvwxyz0123456789';
  let out = '';
  for (const b of bytes) out += alphabet[b % alphabet.length];
  return `q-${out}`;
}

export function normalizeUsPhone(input: string): string | null {
  const digits = input.replace(/\D/g, '');
  const ten = digits.length === 11 && digits.startsWith('1') ? digits.slice(1) : digits;
  if (ten.length !== 10) return null;
  return `(${ten.slice(0, 3)}) ${ten.slice(3, 6)}-${ten.slice(6)}`;
}

export function isValidUsZip(input: string): boolean {
  return /^\d{5}(-\d{4})?$/.test(input.trim());
}

export function formatUsPhoneDisplay(digitsOrFormatted: string): string {
  const n = normalizeUsPhone(digitsOrFormatted);
  return n ?? digitsOrFormatted;
}

const SPAM_KEYWORDS = [
  'crypto',
  'bitcoin',
  'viagra',
  'casino',
  'seo services',
  'buy now',
  'click here',
  'make money',
  'nigerian',
];

/** Link count + keyword spam score (0–100). >2 links ⇒ spam. */
export function scoreInquirySpam(description: string): { score: number; reasons: string[] } {
  const reasons: string[] = [];
  let score = 0;
  const links = description.match(/https?:\/\/|www\./gi) ?? [];
  if (links.length > 2) {
    score += 60;
    reasons.push('too_many_links');
  } else if (links.length > 0) {
    score += links.length * 10;
  }
  const lower = description.toLowerCase();
  for (const kw of SPAM_KEYWORDS) {
    if (lower.includes(kw)) {
      score += 25;
      reasons.push(`keyword:${kw}`);
    }
  }
  return { score: Math.min(100, score), reasons };
}

export function isOutsideServiceArea(
  mode: QuoteFormConfig['serviceAreaMode'],
  chips: string[],
  zip: string | null | undefined,
  cityHint?: string | null,
): boolean {
  if (mode === 'city_only') return false;
  if (!chips.length) return false;
  const normalized = chips.map((c) => c.trim().toLowerCase());
  if (zip) {
    const z5 = zip.trim().slice(0, 5);
    if (normalized.some((c) => c === z5 || c.includes(z5))) return false;
  }
  if (cityHint) {
    const city = cityHint.trim().toLowerCase();
    if (normalized.some((c) => c === city || c.includes(city))) return false;
  }
  // ZIP provided but not in list
  return Boolean(zip);
}

export function privacyNoticeHtml(config: QuoteFormConfig, retentionMonths: number): string {
  const contact =
    config.publicReplyEmail ||
    config.publicPhone ||
    'the business contact details on this page';
  return [
    `<p><strong>${escapeHtml(config.businessName)}</strong> collects the information you enter on this quote request form (name, contact details, job description, and optional photos) so they can respond to your estimate request.</p>`,
    `<p>Only ${escapeHtml(config.businessName)} and the platform that hosts this form can see your submission. It is not sold or used for advertising.</p>`,
    `<p>Inquiries are kept for about ${retentionMonths} months, then deleted. You can ask for deletion earlier via the privacy request page.</p>`,
    `<p>Contact: ${escapeHtml(String(contact))}.</p>`,
  ].join('\n');
}

export function escapeHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

export const publicInquirySubmitSchema = z
  .object({
    name: z.string().min(1).max(120),
    phone: z.string().max(30).optional().nullable(),
    email: z.string().email().optional().nullable().or(z.literal('')),
    service: z.string().min(1).max(80),
    zip: z.string().max(10).optional().nullable(),
    street: z.string().max(200).optional().nullable(),
    description: z.string().min(20).max(1000),
    timing: z.string().max(60).optional().nullable(),
    contactPref: z.enum(CONTACT_PREFS).optional().nullable(),
    customAnswers: z.record(z.string().max(500)).optional(),
    turnstile_token: z.string().optional(),
    /** Honeypot */
    company_website: z.string().max(200).optional(),
    /** Form rendered at (ms epoch) for time-to-submit */
    form_loaded_at: z.coerce.number().optional(),
    embed_origin: z.string().max(300).optional().nullable(),
    is_self_test: z.boolean().optional(),
    is_synthetic: z.boolean().optional(),
  })
  .refine((d) => Boolean(d.phone?.trim() || d.email?.trim()), {
    message: 'Phone or email is required',
    path: ['phone'],
  });

export type PublicInquirySubmit = z.infer<typeof publicInquirySubmitSchema>;
